import { useCallback, useEffect, useRef, useState } from 'react';
import { GetCurrent, GetEmail, GetSetup } from '../../Api/getData';
import { setupMonitoringVessels, vesselKey } from './monitoringData';

export default function useMonitoringData() {
    const [data, setData] = useState([]);
    const [setupLoading, setSetupLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const activeRequest = useRef(null);
    const refreshQueued = useRef(false);

    const refresh = useCallback(async function fetchData(options = {}) {
        if (activeRequest.current) {
            if (options.queue) refreshQueued.current = true;
            return;
        }
        const controller = new AbortController();
        activeRequest.current = controller;
        const isActive = () => activeRequest.current === controller && !controller.signal.aborted;
        const updateVessel = (key, patch) => {
            if (isActive()) setData(previous => previous.map(vessel => vesselKey(vessel) === key ? { ...vessel, ...patch } : vessel));
        };

        setSetupLoading(true);
        setLoadError('');
        try {
            const setup = await GetSetup(undefined, { signal: controller.signal, timeout: 60000 });
            if (!isActive()) return;
            if (!Array.isArray(setup)) throw new Error('Invalid vessel setup response');
            const vessels = setupMonitoringVessels(setup);
            // Publish the directory before starting either sensor or inbox requests.
            // Keep existing positions visible during a refresh, with their report dates.
            setData(previous => {
                const previousByKey = new Map(previous.map(vessel => [vesselKey(vessel), vessel]));
                return vessels.map(vessel => ({ ...vessel, email: previousByKey.get(vesselKey(vessel))?.email || null }));
            });
            setSetupLoading(false);

            let currentFailures = 0;
            let emailFailures = 0;
            const loadPositions = async () => {
                await Promise.all(vessels.map(async vessel => {
                    try {
                        const email = await GetEmail({ ObjectCode: vessel.ShipCode }, controller.signal, { timeout: 120000 });
                        updateVessel(vesselKey(vessel), { email, emailState: 'ready' });
                    } catch {
                        if (!isActive()) return;
                        emailFailures += 1;
                        updateVessel(vesselKey(vessel), { emailState: 'error' });
                    }
                }));
            };
            const loadReadings = async () => {
                // Preserve the existing sequential GetCurrent calls.
                for (const vessel of vessels) {
                    if (!isActive()) return;
                    try {
                        const current = await GetCurrent({ AccountId: vessel.KodeCabang, ObjectCode: vessel.ShipCode }, {
                            signal: controller.signal, timeout: 60000,
                        });
                        if (!Array.isArray(current)) throw new Error('Invalid sensor response');
                        updateVessel(vesselKey(vessel), { current, currentState: current.length ? 'ready' : 'empty' });
                    } catch {
                        if (!isActive()) return;
                        currentFailures += 1;
                        updateVessel(vesselKey(vessel), { current: [], currentState: 'error' });
                    }
                }
            };
            // Neither resource blocks vessel identities or the other resource.
            await Promise.all([loadPositions(), loadReadings()]);
            if (isActive() && (currentFailures || emailFailures)) {
                const failures = [
                    currentFailures ? `${currentFailures} vessel sensor request(s) failed` : '',
                    emailFailures ? `${emailFailures} position request(s) failed` : '',
                ].filter(Boolean).join('; ');
                setLoadError(`${failures}. Please try Refresh. Existing position reports retain their report dates.`);
            }
        } catch {
            if (isActive()) setLoadError('Unable to load the configured vessel list. Please try Refresh.');
        } finally {
            if (isActive()) {
                setSetupLoading(false);
                activeRequest.current = null;
                if (refreshQueued.current) {
                    refreshQueued.current = false;
                    fetchData();
                }
            }
        }
    }, []);

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, 1800000);
        return () => {
            clearInterval(interval);
            activeRequest.current?.abort();
            activeRequest.current = null;
            refreshQueued.current = false;
        };
    }, [refresh]);

    const currentLoading = data.some(vessel => vessel.currentState === 'loading');
    const emailLoading = data.some(vessel => vessel.emailState === 'loading');
    return { data, setupLoading, currentLoading, emailLoading, loadError, refresh };
}
